// components/UsersTab.tsx - Complete User Management Component
import React, { useState } from 'react';
import { 
  Search, UserPlus, User, Map, Badge, Edit, Trash, Eye, EyeOff, 
  Lock, Mail, Phone, MapPin, Calendar, Shield, Users, Activity,
  X, CheckCircle, AlertCircle, Key, Building, Settings, Clock
} from 'lucide-react';
import { User as UserType, Area, Role, Permission, UserTabType } from '../types';
import { roles, allPermissions } from '../data';
import { getUserStatusColor } from '../utils';

interface UsersTabProps {
  users: UserType[];
  areas: Area[];
  onUpdateUser?: (user: UserType) => void;
  onDeleteUser?: (userId: number) => void;
  onCreateUser?: (user: Omit<UserType, 'id'>) => void;
  onUpdateArea?: (area: Area) => void;
  onDeleteArea?: (areaId: number) => void;
  onCreateArea?: (area: Omit<Area, 'id'>) => void;
}

const UsersTab: React.FC<UsersTabProps> = ({ 
  users, 
  areas,
  onUpdateUser,
  onDeleteUser,
  onCreateUser,
  onUpdateArea,
  onDeleteArea,
  onCreateArea
}) => {
  const [activeUserTab, setActiveUserTab] = useState<UserTabType>('users');
  const [searchTerm, setSearchTerm] = useState('');
  const [userFilter, setUserFilter] = useState('all');
  const [showUserModal, setShowUserModal] = useState(false);
  const [showAreaModal, setShowAreaModal] = useState(false);
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserType | null>(null);
  const [selectedArea, setSelectedArea] = useState<Area | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{
    type: 'deleteUser' | 'deleteArea';
    id: number;
    name: string;
  } | null>(null);

  // Form states for new/edit user
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'collector' as UserType['role'],
    assignedAreas: [] as number[],
    permissions: [] as string[],
    password: '',
    status: 'active' as UserType['status']
  });

  // Form states for new/edit area
  const [areaForm, setAreaForm] = useState({
    name: '',
    code: '',
    propertyCount: 0,
    userCount: 0
  });

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.phone.includes(searchTerm);
    
    if (userFilter === 'all') return matchesSearch;
    if (userFilter === 'active') return matchesSearch && user.status === 'active';
    if (userFilter === 'inactive') return matchesSearch && user.status === 'inactive';
    return matchesSearch && user.role === userFilter;
  });

  const handleUserClick = (user: UserType) => {
    setSelectedUser(user);
    setUserForm({
      name: user.name,
      email: user.email,
      phone: user.phone,
      role: user.role,
      assignedAreas: user.assignedAreas,
      permissions: user.permissions,
      password: '',
      status: user.status
    });
    setIsEditing(true);
    setShowUserModal(true);
  };

  const handleAreaClick = (area: Area) => {
    setSelectedArea(area);
    setAreaForm({
      name: area.name,
      code: area.code,
      propertyCount: area.propertyCount,
      userCount: area.userCount
    });
    setShowAreaModal(true);
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    setUserForm({
      name: '',
      email: '',
      phone: '',
      role: 'collector',
      assignedAreas: [],
      permissions: [],
      password: '',
      status: 'active'
    });
    setIsEditing(false);
    setShowPasswordField(true);
    setShowUserModal(true);
  };

  const handleCreateArea = () => {
    setSelectedArea(null);
    setAreaForm({
      name: '',
      code: '',
      propertyCount: 0,
      userCount: 0
    });
    setShowAreaModal(true);
  };

  const handleSaveUser = () => {
    const userData = {
      ...userForm,
      id: selectedUser?.id || Date.now(),
      avatar: getRandomAvatar(userForm.role),
      lastLogin: selectedUser?.lastLogin || new Date().toLocaleString(),
      createdDate: selectedUser?.createdDate || new Date().toISOString().split('T')[0]
    };

    if (isEditing && selectedUser && onUpdateUser) {
      onUpdateUser(userData as UserType);
    } else if (!isEditing && onCreateUser) {
      onCreateUser(userData);
    }

    setShowUserModal(false);
    setShowPasswordField(false);
  };

  const handleSaveArea = () => {
    const areaData = {
      ...areaForm,
      id: selectedArea?.id || Date.now()
    };

    if (selectedArea && onUpdateArea) {
      onUpdateArea(areaData as Area);
    } else if (onCreateArea) {
      onCreateArea(areaData);
    }

    setShowAreaModal(false);
  };

  const getRandomAvatar = (role: string): string => {
    const avatars = {
      admin: ['👨‍💼', '👩‍💼', '🧑‍💼'],
      supervisor: ['👨‍🏫', '👩‍🏫', '🧑‍🏫'],
      collector: ['👨', '👩', '🧑'],
      driver: ['👷‍♂️', '👷‍♀️', '👷'],
      cleaner: ['🧹', '🧽', '🪣']
    };
    const roleAvatars = avatars[role as keyof typeof avatars] || ['👤'];
    return roleAvatars[Math.floor(Math.random() * roleAvatars.length)];
  };

  const handleDeleteConfirm = () => {
    if (confirmAction) {
      if (confirmAction.type === 'deleteUser') {
        onDeleteUser?.(confirmAction.id);
      } else if (confirmAction.type === 'deleteArea') {
        onDeleteArea?.(confirmAction.id);
      }
    }
    setShowConfirmDialog(false);
    setConfirmAction(null);
  };

  const getRolePermissions = (roleId: string): string[] => {
    const role = roles.find(r => r.id === roleId);
    return role ? role.permissions : [];
  };

  const confirmDelete = () => {
    if (confirmAction) {
      if (confirmAction.type === 'deleteUser') {
        onDeleteUser?.(confirmAction.id);
      } else if (confirmAction.type === 'deleteArea') {
        onDeleteArea?.(confirmAction.id);
      }
    }
    setShowConfirmDialog(false);
    setConfirmAction(null);
  };

  const cancelDelete = () => {
    setShowConfirmDialog(false);
    setConfirmAction(null);
  };

  const getUserStats = () => {
    return {
      total: users.length,
      active: users.filter(u => u.status === 'active').length,
      inactive: users.filter(u => u.status === 'inactive').length,
      admins: users.filter(u => u.role === 'admin').length,
      collectors: users.filter(u => u.role === 'collector').length,
      supervisors: users.filter(u => u.role === 'supervisor').length,
      drivers: users.filter(u => u.role === 'driver').length
    };
  };

  const userStats = getUserStats();

  return (
    <div className="space-y-6">
      {/* User Management Sub-tabs */}
      <div className="bg-white rounded-2xl p-2 flex space-x-2 shadow-sm">
        {[
          { id: 'users' as UserTabType, name: 'Users', icon: User, count: users.length },
          { id: 'areas' as UserTabType, name: 'Areas', icon: Map, count: areas.length },
          { id: 'roles' as UserTabType, name: 'Roles', icon: Badge, count: roles.length }
        ].map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveUserTab(tab.id)}
              className={`flex-1 flex items-center justify-center space-x-2 py-3 rounded-xl transition-all duration-200 ${
                activeUserTab === tab.id
                  ? 'bg-indigo-600 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Icon className="w-5 h-5" />
              <span className="font-medium">{tab.name}</span>
              <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">
                {tab.count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Users Section */}
      {activeUserTab === 'users' && (
        <div className="space-y-6">
          {/* User Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-2">
                <Users className="w-8 h-8 text-indigo-600" />
                <span className="text-2xl font-bold text-indigo-600">{userStats.active}</span>
              </div>
              <div className="text-sm text-indigo-700 font-medium">Active Users</div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-2">
                <User className="w-8 h-8 text-green-600" />
                <span className="text-2xl font-bold text-green-600">{userStats.collectors}</span>
              </div>
              <div className="text-sm text-green-700 font-medium">Collectors</div>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-2">
                <Shield className="w-8 h-8 text-blue-600" />
                <span className="text-2xl font-bold text-blue-600">{userStats.admins}</span>
              </div>
              <div className="text-sm text-blue-700 font-medium">Administrators</div>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-2">
                <Activity className="w-8 h-8 text-purple-600" />
                <span className="text-2xl font-bold text-purple-600">{areas.length}</span>
              </div>
              <div className="text-sm text-purple-700 font-medium">Areas Managed</div>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search users by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 pr-4 py-4 w-full border border-gray-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white shadow-sm"
              />
            </div>

            <div className="flex flex-wrap gap-2">
              <span className="text-sm font-medium text-gray-600 self-center mr-2">Filter:</span>
              {[
                { id: 'all', name: 'All', count: users.length },
                { id: 'active', name: 'Active', count: userStats.active },
                { id: 'inactive', name: 'Inactive', count: userStats.inactive },
                ...roles.map(role => ({
                  id: role.id,
                  name: role.name,
                  count: users.filter(u => u.role === role.id).length
                }))
              ].map(filter => (
                <button
                  key={filter.id}
                  onClick={() => setUserFilter(filter.id)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                    userFilter === filter.id
                      ? 'bg-indigo-600 text-white shadow-lg'
                      : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {filter.name} ({filter.count})
                </button>
              ))}
            </div>
          </div>

          {/* Add New User Button */}
          <button
            onClick={handleCreateUser}
            className="w-full bg-gradient-to-r from-indigo-500 to-indigo-600 text-white py-4 rounded-2xl font-semibold flex items-center justify-center space-x-2 hover:from-indigo-600 hover:to-indigo-700 transition-all shadow-lg"
          >
            <UserPlus className="w-5 h-5" />
            <span>Add New User</span>
          </button>

          {/* Users List */}
          <div className="space-y-4">
            {filteredUsers.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-3xl border border-gray-200">
                <div className="text-gray-400 mb-4">
                  <Users className="w-16 h-16 mx-auto" />
                </div>
                <h3 className="text-lg font-semibold text-gray-600 mb-2">No users found</h3>
                <p className="text-gray-500">Try adjusting your search terms or filters</p>
              </div>
            ) : (
              filteredUsers.map(user => {
                const role = roles.find(r => r.id === user.role);
                return (
                  <div 
                    key={user.id} 
                    className="bg-white rounded-3xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow duration-200"
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex items-center space-x-4">
                        <div className="w-14 h-14 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center text-2xl">
                          {user.avatar}
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">{user.name}</h3>
                          <div className="flex items-center space-x-2 text-sm text-gray-600">
                            <Mail className="w-4 h-4" />
                            <span>{user.email}</span>
                          </div>
                          <div className="flex items-center space-x-2 text-sm text-gray-600 mt-1">
                            <Phone className="w-4 h-4" />
                            <span>{user.phone}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-end space-y-2">
                        <div className={`px-3 py-1 rounded-full text-xs font-semibold border ${getUserStatusColor(user.status)}`}>
                          {user.status}
                        </div>
                        {role && (
                          <div className={`px-3 py-1 rounded-full text-xs font-medium border ${role.color}`}>
                            <span className="mr-1">{role.icon}</span>
                            {role.name}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div className="bg-blue-50 rounded-xl p-3">
                        <div className="text-xs text-gray-500 mb-1">Assigned Areas</div>
                        <div className="text-sm font-medium text-blue-700">
                          {user.assignedAreas.length > 0 
                            ? `${user.assignedAreas.length} Area${user.assignedAreas.length > 1 ? 's' : ''}`
                            : 'No areas assigned'}
                        </div>
                      </div>
                      <div className="bg-green-50 rounded-xl p-3">
                        <div className="text-xs text-gray-500 mb-1">Last Login</div>
                        <div className="text-sm font-medium text-green-700">{user.lastLogin}</div>
                      </div>
                    </div>

                    {user.assignedAreas.length > 0 && (
                      <div className="p-3 bg-gray-50 rounded-xl mb-4">
                        <div className="text-xs text-gray-500 mb-2">Areas:</div>
                        <div className="flex flex-wrap gap-2">
                          {user.assignedAreas.map(areaId => {
                            const area = areas.find(a => a.id === areaId);
                            return area ? (
                              <span key={areaId} className="px-2 py-1 bg-white rounded-lg text-xs font-medium text-gray-700 border border-gray-200">
                                <MapPin className="w-3 h-3 inline mr-1" />
                                {area.name}
                              </span>
                            ) : null;
                          })}
                        </div>
                      </div>
                    )}

                    {/* Permissions Preview */}
                    <div className="p-3 bg-purple-50 rounded-xl mb-4">
                      <div className="text-xs text-gray-500 mb-2">Permissions:</div>
                      <div className="flex flex-wrap gap-2">
                        {user.permissions[0] === 'all' ? (
                          <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-lg text-xs font-medium">
                            <Key className="w-3 h-3 inline mr-1" />
                            All System Permissions
                          </span>
                        ) : (
                          user.permissions.slice(0, 3).map(permId => {
                            const perm = allPermissions.find(p => p.id === permId);
                            return perm ? (
                              <span key={permId} className="px-2 py-1 bg-white rounded-lg text-xs font-medium text-gray-700 border border-gray-200">
                                {perm.name}
                              </span>
                            ) : null;
                          })
                        )}
                        {user.permissions.length > 3 && user.permissions[0] !== 'all' && (
                          <span className="px-2 py-1 bg-gray-100 rounded-lg text-xs font-medium text-gray-600">
                            +{user.permissions.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <div className="flex items-center space-x-4">
                        <button
                          onClick={() => handleUserClick(user)}
                          className="flex items-center space-x-1 text-indigo-600 hover:text-indigo-700 transition-colors"
                        >
                          <Edit className="w-4 h-4" />
                          <span className="text-sm font-medium">Edit</span>
                        </button>
                        <button
                          onClick={() => {
                            setConfirmAction({
                              type: 'deleteUser',
                              id: user.id,
                              name: user.name
                            });
                            setShowConfirmDialog(true);
                          }}
                          className="flex items-center space-x-1 text-red-600 hover:text-red-700 transition-colors"
                        >
                          <Trash className="w-4 h-4" />
                          <span className="text-sm font-medium">Delete</span>
                        </button>
                      </div>
                      <div className="flex items-center space-x-2 text-xs text-gray-400">
                        <Calendar className="w-3 h-3" />
                        <span>Created: {user.createdDate}</span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Areas Section */}
      {activeUserTab === 'areas' && (
        <div className="space-y-6">
          {/* Area Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-2">
                <Map className="w-8 h-8 text-blue-600" />
                <span className="text-2xl font-bold text-blue-600">{areas.length}</span>
              </div>
              <div className="text-sm text-blue-700 font-medium">Total Areas</div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-2">
                <Building className="w-8 h-8 text-green-600" />
                <span className="text-2xl font-bold text-green-600">
                  {areas.reduce((sum, area) => sum + area.propertyCount, 0)}
                </span>
              </div>
              <div className="text-sm text-green-700 font-medium">Total Properties</div>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-2">
                <Users className="w-8 h-8 text-purple-600" />
                <span className="text-2xl font-bold text-purple-600">
                  {areas.reduce((sum, area) => sum + area.userCount, 0)}
                </span>
              </div>
              <div className="text-sm text-purple-700 font-medium">Assigned Users</div>
            </div>
          </div>

          {/* Add New Area Button */}
          <button
            onClick={handleCreateArea}
            className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-4 rounded-2xl font-semibold flex items-center justify-center space-x-2 hover:from-blue-600 hover:to-blue-700 transition-all shadow-lg"
          >
            <MapPin className="w-5 h-5" />
            <span>Add New Area</span>
          </button>

          {/* Areas List */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {areas.map(area => (
              <div 
                key={area.id}
                className="bg-white rounded-3xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => handleAreaClick(area)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-2xl flex items-center justify-center">
                      <MapPin className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900">{area.name}</h3>
                      <p className="text-sm text-gray-500">Code: {area.code}</p>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setConfirmAction({
                        type: 'deleteArea',
                        id: area.id,
                        name: area.name
                      });
                      setShowConfirmDialog(true);
                    }}
                    className="text-red-600 hover:text-red-700 transition-colors"
                  >
                    <Trash className="w-4 h-4" />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-green-50 rounded-xl p-3">
                    <div className="text-xs text-gray-500 mb-1">Properties</div>
                    <div className="text-lg font-bold text-green-600">{area.propertyCount}</div>
                  </div>
                  <div className="bg-indigo-50 rounded-xl p-3">
                    <div className="text-xs text-gray-500 mb-1">Assigned Users</div>
                    <div className="text-lg font-bold text-indigo-600">{area.userCount}</div>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-100">
                  <div className="text-xs text-gray-500 mb-2">Assigned to:</div>
                  <div className="flex flex-wrap gap-2">
                    {users
                      .filter(u => u.assignedAreas.includes(area.id))
                      .slice(0, 3)
                      .map(user => (
                        <span key={user.id} className="px-2 py-1 bg-gray-100 rounded-lg text-xs font-medium text-gray-700">
                          {user.avatar} {user.name}
                        </span>
                      ))}
                    {users.filter(u => u.assignedAreas.includes(area.id)).length > 3 && (
                      <span className="px-2 py-1 bg-gray-100 rounded-lg text-xs font-medium text-gray-700">
                        +{users.filter(u => u.assignedAreas.includes(area.id)).length - 3} more
                      </span>
                    )}
                    {users.filter(u => u.assignedAreas.includes(area.id)).length === 0 && (
                      <span className="px-2 py-1 bg-red-100 text-red-700 rounded-lg text-xs font-medium">
                        No users assigned
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Roles Section */}
      {activeUserTab === 'roles' && (
        <div className="space-y-6">
          <div className="bg-white rounded-3xl p-6 border border-gray-200 shadow-sm">
            <h3 className="text-xl font-bold text-gray-900 mb-6">System Roles & Permissions</h3>
            
            <div className="space-y-4">
              {roles.map(role => (
                <div key={role.id} className="border border-gray-200 rounded-2xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">{role.icon}</span>
                      <div>
                        <h4 className="font-bold text-gray-900">{role.name}</h4>
                        <p className="text-sm text-gray-500">
                          {users.filter(u => u.role === role.id).length} users assigned
                        </p>
                      </div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-xs font-medium border ${role.color}`}>
                      {role.id}
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-xl p-3">
                    <div className="text-xs text-gray-500 mb-2">Permissions:</div>
                    <div className="flex flex-wrap gap-2">
                      {role.permissions[0] === 'all' ? (
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-lg text-xs font-medium">
                          <Key className="w-3 h-3 inline mr-1" />
                          All System Permissions
                        </span>
                      ) : (
                        role.permissions.map(permId => {
                          const perm = allPermissions.find(p => p.id === permId);
                          return perm ? (
                            <span key={permId} className="px-2 py-1 bg-white rounded-lg text-xs font-medium text-gray-700 border border-gray-200">
                              {perm.name}
                            </span>
                          ) : null;
                        })
                      )}
                    </div>
                  </div>

                  {/* Users with this role */}
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="text-xs text-gray-500 mb-2">Users with this role:</div>
                    <div className="flex flex-wrap gap-2">
                      {users
                        .filter(u => u.role === role.id)
                        .slice(0, 5)
                        .map(user => (
                          <span key={user.id} className="px-2 py-1 bg-blue-100 text-blue-700 rounded-lg text-xs font-medium">
                            {user.avatar} {user.name}
                          </span>
                        ))}
                      {users.filter(u => u.role === role.id).length > 5 && (
                        <span className="px-2 py-1 bg-gray-100 rounded-lg text-xs font-medium text-gray-600">
                          +{users.filter(u => u.role === role.id).length - 5} more
                        </span>
                      )}
                      {users.filter(u => u.role === role.id).length === 0 && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-lg text-xs font-medium">
                          No users assigned
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* User Modal */}
      {showUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-2xl rounded-3xl p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {isEditing ? 'Edit User' : 'Add New User'}
              </h2>
              <button
                onClick={() => {
                  setShowUserModal(false);
                  setShowPasswordField(false);
                }}
                className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-2xl flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                    <input
                      type="text"
                      value={userForm.name}
                      onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                      className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Enter full name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                    <input
                      type="email"
                      value={userForm.email}
                      onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                      className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Enter email address"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                    <input
                      type="tel"
                      value={userForm.phone}
                      onChange={(e) => setUserForm({ ...userForm, phone: e.target.value })}
                      className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Enter phone number"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <select
                      value={userForm.status}
                      onChange={(e) => setUserForm({ ...userForm, status: e.target.value as 'active' | 'inactive' })}
                      className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Role and Permissions */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Role & Permissions</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                  <select
                    value={userForm.role}
                    onChange={(e) => {
                      const newRole = e.target.value as UserType['role'];
                      setUserForm({ 
                        ...userForm, 
                        role: newRole,
                        permissions: getRolePermissions(newRole)
                      });
                    }}
                    className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    {roles.map(role => (
                      <option key={role.id} value={role.id}>
                        {role.icon} {role.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Permissions</label>
                  <div className="bg-gray-50 rounded-xl p-4 max-h-40 overflow-y-auto">
                    {userForm.permissions[0] === 'all' ? (
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <span className="font-medium text-green-700">All System Permissions</span>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {Object.entries(
                          allPermissions.reduce((acc, perm) => {
                            if (!acc[perm.category]) acc[perm.category] = [];
                            acc[perm.category].push(perm);
                            return acc;
                          }, {} as Record<string, Permission[]>)
                        ).map(([category, perms]) => (
                          <div key={category}>
                            <div className="font-medium text-gray-700 text-sm mb-1">{category}</div>
                            <div className="flex flex-wrap gap-2">
                              {perms.map(perm => (
                                <span 
                                  key={perm.id}
                                  className={`px-2 py-1 rounded-lg text-xs font-medium ${
                                    userForm.permissions.includes(perm.id)
                                      ? 'bg-indigo-100 text-indigo-700'
                                      : 'bg-gray-200 text-gray-600'
                                  }`}
                                >
                                  {perm.name}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Area Assignment */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Area Assignment</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Assigned Areas</label>
                  <div className="bg-gray-50 rounded-xl p-4 max-h-32 overflow-y-auto">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {areas.map(area => (
                        <label key={area.id} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={userForm.assignedAreas.includes(area.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setUserForm({
                                  ...userForm,
                                  assignedAreas: [...userForm.assignedAreas, area.id]
                                });
                              } else {
                                setUserForm({
                                  ...userForm,
                                  assignedAreas: userForm.assignedAreas.filter(id => id !== area.id)
                                });
                              }
                            }}
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="text-sm text-gray-700">{area.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Password Section (for new users or when editing) */}
              {(!isEditing || showPasswordField) && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Security</h3>
                    {isEditing && (
                      <button
                        onClick={() => setShowPasswordField(!showPasswordField)}
                        className="text-sm text-indigo-600 hover:text-indigo-700"
                      >
                        {showPasswordField ? 'Cancel Password Change' : 'Change Password'}
                      </button>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {isEditing ? 'New Password' : 'Password'}
                    </label>
                    <div className="relative">
                      <input
                        type="password"
                        value={userForm.password}
                        onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                        className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder={isEditing ? "Enter new password" : "Enter password"}
                      />
                      <Lock className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Actions */}
            <div className="flex space-x-4 mt-8">
              <button
                onClick={() => {
                  setShowUserModal(false);
                  setShowPasswordField(false);
                }}
                className="flex-1 py-3 px-6 border-2 border-gray-200 text-gray-700 rounded-2xl font-semibold hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveUser}
                disabled={!userForm.name || !userForm.email || !userForm.phone || (!isEditing && !userForm.password)}
                className="flex-1 py-3 px-6 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white rounded-2xl font-semibold disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed hover:from-indigo-600 hover:to-indigo-700 transition-all"
              >
                {isEditing ? 'Update User' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Area Modal */}
      {showAreaModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md rounded-3xl p-6 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {selectedArea ? 'Edit Area' : 'Add New Area'}
              </h2>
              <button
                onClick={() => setShowAreaModal(false)}
                className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-2xl flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Area Name</label>
                <input
                  type="text"
                  value={areaForm.name}
                  onChange={(e) => setAreaForm({ ...areaForm, name: e.target.value })}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter area name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Area Code</label>
                <input
                  type="text"
                  value={areaForm.code}
                  onChange={(e) => setAreaForm({ ...areaForm, code: e.target.value })}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter area code (e.g., JH-S1)"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Property Count</label>
                  <input
                    type="number"
                    value={areaForm.propertyCount}
                    onChange={(e) => setAreaForm({ ...areaForm, propertyCount: parseInt(e.target.value) || 0 })}
                    className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">User Count</label>
                  <input
                    type="number"
                    value={areaForm.userCount}
                    onChange={(e) => setAreaForm({ ...areaForm, userCount: parseInt(e.target.value) || 0 })}
                    className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0"
                  />
                </div>
              </div>
            </div>

            <div className="flex space-x-4 mt-8">
              <button
                onClick={() => setShowAreaModal(false)}
                className="flex-1 py-3 px-6 border-2 border-gray-200 text-gray-700 rounded-2xl font-semibold hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveArea}
                disabled={!areaForm.name || !areaForm.code}
                className="flex-1 py-3 px-6 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl font-semibold disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed hover:from-blue-600 hover:to-blue-700 transition-all"
              >
                {selectedArea ? 'Update Area' : 'Create Area'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && confirmAction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md rounded-3xl p-6 shadow-2xl">
            <div className="flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            
            <div className="text-center mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Confirm Deletion</h3>
              <p className="text-gray-600">
                Are you sure you want to delete{' '}
                <span className="font-semibold">"{confirmAction.name}"</span>?{' '}
                This action cannot be undone.
              </p>
            </div>

            <div className="flex space-x-4">
              <button
                onClick={cancelDelete}
                className="flex-1 py-3 px-6 border-2 border-gray-200 text-gray-700 rounded-2xl font-semibold hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 py-3 px-6 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-2xl font-semibold hover:from-red-600 hover:to-red-700 transition-all"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersTab;